import { GlobalMessages } from '@shared/localization/messages';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import sumBy from 'lodash/sumBy';
import { useCallback, useMemo, useState } from 'react';

import { useEstimationItemResourceQueryActions } from '@/api/hooks/estimationItemResourceQueryActions';
import type { UpsertEstimationItemResourceRequest } from '@/api/schema/data-contracts';
import { ApiEstimationStage } from '@/api/schema/data-contracts';
import { AssumptionsModal } from '@/components/Assumption';
import TableSkeleton from '@/components/atoms/TableSkeleton';
import Search from '@/components/molecules/Search';
import { CommentsModal } from '@/components/organism/Comments/CommentsModal';
import { ExportButtonsSelector } from '@/components/organism/DataTable/components/exportSelector';
import { eExportFormat } from '@/components/organism/DataTable/model';
import type { EstimationExportButtonsProps } from '@/components/organism/EstimationExportButtons';
import { messages as sidebarMessageDefinitions } from '@/components/organism/Sidebar/messages';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';
import { useDebouncedBy } from '@/hooks/useDebounced';
import { useEstimationExport } from '@/hooks/useEstimationExport';
import { useHasEstimationPermission } from '@/hooks/useHasEstimationPermission';
import type { EstimationItemsGroupedTableProps } from '@/pages/estimation/detail/EstimationItemsGroupedTables';
import { Route } from '@/routes/estimation/detail/$estimationId/$tabId';
import type { OmitSafely } from '@/types';
import { sortResourceTypes } from '@/utils/sorting';

import { Layout } from '../../Layout';
import { getResourceEstimationItemDetail } from '../../mappers/estimationItemDetailMapper';
import { estimationTabMessageDefinitions } from '../../messages';
import type {
  EstimationItemActionCallback,
  EstimationItemsEditFormModel,
  ResourceEstimationItemsTableRecord,
} from '../../model';
import { useResourceEstimationItemsTableRecords } from '../../useEstimationItemsTableRecords';
import { useResourceDisplayNameRename } from '../../useResourceDisplayNameRename';
import EstimationItemDetailModal from './EstimationItemDetailModal';
import { TopLevelEstimationItemsForm } from './TopLevelEstimationItemsForm';

const emptyArray: Array<ResourceEstimationItemsTableRecord> = [];

const EstimationDetailResourcingTab = () => {
  const { estimationId } = Route.useLoaderData();
  const toast = useToast();
  const [searchText, setSearchText] = useState('');
  const sidebarMessages = useMessages(sidebarMessageDefinitions);
  const tabMessages = useMessages(estimationTabMessageDefinitions);
  const globalMessages = useMessages(GlobalMessages);

  const {
    isLoading: isLoadingTableRecords,
    data,
    getResourceEstimationItemsTableRecordsByParent: getEstimationItemsTableRecordsByParent,
  } = useResourceEstimationItemsTableRecords(estimationId, { shouldFetchResources: true });

  const { useUpdateResourcesOfEstimationItem } = useEstimationItemResourceQueryActions();
  const updateResourcesOfEstimationItemMutation = useUpdateResourcesOfEstimationItem();

  const hasEditPermission = useHasEstimationPermission(estimationId, 'CAN_EDIT');

  const [estimationItemIdForAssumptionsModal, setEstimationItemIdForAssumptionsModal] =
    useState<string>();
  const [estimationItemIdForCommentsModal, setEstimationItemIdForCommentsModal] =
    useState<string>();
  const [estimationItemIdsForDetailsModal, setEstimationItemIdsForDetailsModal] = useState<
    string[]
  >([]);

  const { debounceBy: debounceResourcesMutationBy } = useDebouncedBy(
    updateResourcesOfEstimationItemMutation.mutate,
    2000,
  );

  const topLevelEstimationItems = useMemo(
    () => getEstimationItemsTableRecordsByParent(undefined).filter((item) => !item.isOmitted),
    [getEstimationItemsTableRecordsByParent],
  );

  const calculatedResultByEstimationItemId = useMemo(
    () =>
      new Map<string, number>(
        data?.estimationItems.map((item) => [item.id, item.calculatedResult ?? 0]),
      ),
    [data?.estimationItems],
  );

  const exportProps = useMemo<OmitSafely<EstimationExportButtonsProps, 'parentEstimationItemId'>>(
    () => ({
      estimationId,
      title: `${sidebarMessages.detailedEstimation()} - ${tabMessages.resourceLabel()}`,
      outputOptionsArray: [
        {
          showResourcePurposeEstimationItems: true,
          showEstimateColumn: true,
          showResourceColumns: true,
        },
      ],
    }),
    [estimationId, sidebarMessages, tabMessages],
  );

  const estimationExporter = useEstimationExport(exportProps);
  const handleExportButtonClick = useCallback(
    (format: eExportFormat) => estimationExporter(format, undefined),
    [estimationExporter],
  );

  const exportConfiguration = useMemo<eExportFormat[]>(
    () => [
      eExportFormat.EXCEL,
      eExportFormat.PDF,
      eExportFormat.CSV,
      eExportFormat.JSON,
      eExportFormat.PRINT,
    ],
    [],
  );

  const sortedResourceTypes = useMemo(
    () =>
      data
        ? sortResourceTypes(
            data.estimationModel.resourceTypes,
            data.estimationModel.resourceTypeGroups,
          )
        : [],
    [data],
  );

  const {
    effectiveResourceDisplayNames,
    hasPersistedOverrides,
    onResourceDisplayNameRename,
    resetResourceDisplayNames,
  } = useResourceDisplayNameRename({
    estimation: data?.estimation,
    projectName: data?.project.name,
    sortedResourceTypes,
  });

  if (isLoadingTableRecords) {
    return (
      <Layout>
        <TableSkeleton />
      </Layout>
    );
  }

  if (data === undefined) {
    return (
      <Layout>
        <TableSkeleton />
      </Layout>
    );
  }

  if (!data.resources) {
    throw new Error('Resources cannot be undefined.');
  }

  const isReadOnly =
    data.estimation.stage === ApiEstimationStage.IN_REVIEW ||
    data.estimation.stage === ApiEstimationStage.APPROVED ||
    !hasEditPermission;

  const onAssumptionsClicked: EstimationItemActionCallback = setEstimationItemIdForAssumptionsModal;
  const onCommentsClicked: EstimationItemActionCallback = setEstimationItemIdForCommentsModal;
  const onDetailsClicked: EstimationItemActionCallback = (estimationItemId: string) => {
    setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal, estimationItemId]);
  };

  const tableProps: OmitSafely<
    EstimationItemsGroupedTableProps,
    'estimationItems' | 'control' | 'onIncludeClicked' | 'searchText' | 'formModelEstimationItems'
  > = {
    estimationModel: data.estimationModel,
    methodologies: data.methodologies,
    estimationUnit: data.estimation.unit,
    showButtons: true,
    showMethodology: false,
    showResponsible: false,
    showParameters: false,
    showResources: true,
    isReadOnly,
    onAssumptionsClicked,
    onCommentsClicked,
    onDetailsClicked,
    calculatedResultByEstimationItemId,
    onResourceColumnHeaderRename: isReadOnly ? undefined : onResourceDisplayNameRename,
    resourceDisplayNames: effectiveResourceDisplayNames,
  };

  const onSubmit = (formModel: EstimationItemsEditFormModel): Promise<void> => {
    const savedResources = data.resources;
    if (!savedResources) {
      throw new Error('Resources cannot be undefined');
    }

    const formModelItemsToUpsertResources = formModel.estimationItems.filter(
      (formModelEstimationItem) => {
        const formModelItemResources = sortBy(
          formModelEstimationItem.resources,
          (x) => x.resourceTypeId,
        );

        const allResourceTypes = sortResourceTypes(
          data.estimationModel.resourceTypes,
          data.estimationModel.resourceTypeGroups,
        );
        if (formModelItemResources?.length !== allResourceTypes.length) {
          throw new Error('Wrong number of resources');
        }

        const initialEstimationItemResources = sortBy(
          savedResources
            .filter((x) => x.estimationItemId === formModelEstimationItem.id)
            .map((x) => pick(x, ['resourceTypeId', 'value'])),
          (x) => x.resourceTypeId,
        );
        // if there are no resources for the item in DB yet and in form it's set as null for every resource type => we can ignore it
        const itemResourcesUnsetYet =
          initialEstimationItemResources.length === 0 &&
          formModelItemResources.every(
            (formModelItemResource) => formModelItemResource.value === null,
          );
        const hasChanged =
          !isEqual(initialEstimationItemResources, formModelItemResources) &&
          !itemResourcesUnsetYet;
        return hasChanged;
      },
    );

    formModelItemsToUpsertResources.forEach((formModelEstimationItem) => {
      const resourcesUpsertPayload = (
        formModelEstimationItem.resources ?? []
      ).map<UpsertEstimationItemResourceRequest>((x) => pick(x, ['resourceTypeId', 'value']));
      debounceResourcesMutationBy(formModelEstimationItem.id, {
        estimationItemId: formModelEstimationItem.id,
        resources: resourcesUpsertPayload,
      });
    });

    return Promise.resolve();
  };

  const onSubmitDetailsModalForm = async (formModel: EstimationItemsEditFormModel) => {
    if (!formModel.mainEstimationItem) {
      throw new Error('Main estimation item cannot be undefined');
    }

    const createPayload = (
      estimationItemId: string,
      resources: {
        resourceTypeId: string;
        value: number | null;
      }[],
    ): {
      estimationItemId: string;
      resources: UpsertEstimationItemResourceRequest[];
    } => ({
      estimationItemId: estimationItemId,
      resources: [...resources.map((resource) => ({ ...resource }))],
    });

    let upsertItemResourcesPayloads: ReturnType<typeof createPayload>[] = [];
    // create payloads for upserting resources of sub-items from modal
    const upsertSubItemsResourcesPayloads = formModel.estimationItems.map(
      (formModelEstimationItem) =>
        createPayload(formModelEstimationItem.id, formModelEstimationItem.resources!),
    );
    upsertItemResourcesPayloads = [
      ...upsertItemResourcesPayloads,
      ...upsertSubItemsResourcesPayloads,
    ];

    // create payloads for upserting resources of parents and its parents, etc.. by calculating it as sum of its children's resources
    const addUpsertParentsResourcesPayloadsRecursively = (estimationItemId: string) => {
      const estimationItem = data.estimationItems.find((x) => x.id === estimationItemId)!;
      const childResources = getEstimationItemsTableRecordsByParent(estimationItemId)
        .filter((x) => !x.isOmitted)
        // prefer fresh values, fallback to BE values
        .map(
          (x) =>
            upsertItemResourcesPayloads.find((payload) => payload.estimationItemId === x.id)
              ?.resources ?? x.resources!,
        )
        .flatMap((x) => x);

      const allResourceTypes = sortResourceTypes(
        data.estimationModel.resourceTypes,
        data.estimationModel.resourceTypeGroups,
      );
      const parentPayload = createPayload(
        estimationItem.id,
        allResourceTypes.map((resourceType) => ({
          resourceTypeId: resourceType.id,
          value: sumBy(
            childResources.filter((resource) => resource.resourceTypeId === resourceType.id),
            (resource) => resource.value ?? 0,
          ),
        })),
      );
      upsertItemResourcesPayloads.push(parentPayload);

      if (estimationItem.parentId) {
        addUpsertParentsResourcesPayloadsRecursively(estimationItem.parentId);
      }
    };

    addUpsertParentsResourcesPayloadsRecursively(formModel.mainEstimationItem.id);

    const upsertPromises = upsertItemResourcesPayloads.map((payload) =>
      updateResourcesOfEstimationItemMutation.mutateAsync(payload),
    );
    await Promise.all(upsertPromises);

    setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal.slice(0, -1)]);
    toast.showToast(globalMessages.savedSuccessfully(), 'success');
  };

  return (
    <Layout>
      <div className='mt-4 flex items-center gap-2 rounded-xl'>
        {hasEditPermission && hasPersistedOverrides && (
          <button
            type='button'
            className='text-sm underline'
            onClick={() => {
              if (!hasPersistedOverrides) return;
              void resetResourceDisplayNames();
            }}
          >
            {tabMessages.resetResourceNamesToDefaults()}
          </button>
        )}
        <div className='flex-1' />
        <Search handleOnChange={setSearchText} />
        <ExportButtonsSelector
          exportConfiguration={exportConfiguration}
          onExportButtonClick={handleExportButtonClick}
        />
      </div>
      <TopLevelEstimationItemsForm
        topLevelEstimationItems={topLevelEstimationItems}
        methodologies={data.methodologies}
        estimationMethodologyId={data.estimation.methodologyId}
        tableProps={{ ...tableProps, searchText }}
        onSubmit={onSubmit}
      />
      {estimationItemIdsForDetailsModal.map((estimationItemIdForDetail) => (
        <EstimationItemDetailModal
          key={estimationItemIdForDetail}
          open={!!estimationItemIdForDetail}
          onClose={() =>
            setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal.slice(0, -1)])
          }
          onSubmit={onSubmitDetailsModalForm}
          estimationId={estimationId}
          estimationItem={
            estimationItemIdForDetail === undefined
              ? undefined
              : getResourceEstimationItemDetail(estimationItemIdForDetail, data)
          }
          estimationItems={
            estimationItemIdForDetail
              ? getEstimationItemsTableRecordsByParent(estimationItemIdForDetail)
              : emptyArray
          }
          estimationMethodologyId={data.estimation.methodologyId}
          exportProps={exportProps}
          {...tableProps}
        />
      ))}
      <AssumptionsModal
        estimationId={estimationId}
        onClose={() => setEstimationItemIdForAssumptionsModal(undefined)}
        estimationItem={data.estimationItems.find(
          (x) => x.id === estimationItemIdForAssumptionsModal,
        )}
      />
      <CommentsModal
        commentType='ESTIMATION_ITEM'
        commentContext='RESOURCING'
        size='s'
        estimationId={data.estimation.id}
        estimationItem={data.estimationItems.find((x) => x.id === estimationItemIdForCommentsModal)}
        onClose={() => setEstimationItemIdForCommentsModal(undefined)}
      />
    </Layout>
  );
};

export default EstimationDetailResourcingTab;
